import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  putApiPartnersByIdPaymentTermsMutation,
  putApiPartnersByIdPlanningMutation,
  type Options,
  type PartnerDto,
  type PutApiPartnersByIdPaymentTermsData,
  type PutApiPartnersByIdPaymentTermsError,
  type PutApiPartnersByIdPlanningData,
  type PutApiPartnersByIdPlanningError,
} from '@/api';

import { SHIPMENT_FORECAST_KEY } from '../../shipment-actions';

/*
 * Исключение клиента из плана из истории - единственная запись в справочник
 * клиентов с нашей стороны: флаг - наше поле в зеркале 1С. После неё
 * перезапрашиваются клиенты и расчёт плана: исключённый из него пропадает.
 */
export const useSetPartnerPlanning = (): UseMutationResult<
  PartnerDto,
  PutApiPartnersByIdPlanningError,
  Options<PutApiPartnersByIdPlanningData>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    ...putApiPartnersByIdPlanningMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getApiPartners' }],
        }),
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getApiPartnersById' }],
        }),
        queryClient.invalidateQueries({ queryKey: [SHIPMENT_FORECAST_KEY] }),
      ]);
    },
  });
};

/*
 * Условия оплаты клиента - наши поля в зеркале 1С, как исключение из плана. Их
 * показывает и карточка отгрузки, поэтому после записи перезапрашиваются и
 * клиенты, и карточки отгрузок.
 */
export const useSetPartnerPaymentTerms = (): UseMutationResult<
  PartnerDto,
  PutApiPartnersByIdPaymentTermsError,
  Options<PutApiPartnersByIdPaymentTermsData>
> => {
  const queryClient = useQueryClient();

  return useMutation({
    ...putApiPartnersByIdPaymentTermsMutation(),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getApiPartners' }],
        }),
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getApiPartnersById' }],
        }),
        queryClient.invalidateQueries({
          queryKey: [{ _id: 'getApiShipmentsById' }],
        }),
      ]);
    },
  });
};
